import { useState, useEffect } from 'react';
import { adminApi, publicApi } from '../../lib/api';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { ImageUpload } from '../../components/admin/ImageUpload';
import { toast } from 'sonner';
import { resolveImageUrl } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

const emptyProject = {
  title: '',
  location: '',
  description: '',
  status: 'planned',
  image_url: '',
  area: '',
  units: '',
};

export const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState(emptyProject);
  const [saving, setSaving] = useState(false);

  const fetchProjects = async () => {
    try {
      const response = await publicApi.getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleOpenCreate = () => {
    setEditingProject(null);
    setFormData(emptyProject);
    setDialogOpen(true);
  };

  const handleOpenEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      location: project.location,
      description: project.description,
      status: project.status,
      image_url: project.image_url,
      area: project.area || '',
      units: project.units || '',
    });
    setDialogOpen(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStatusChange = (value) => {
    setFormData({ ...formData, status: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image_url) {
      toast.error('Lütfen bir kapak fotoğrafı yükleyin');
      return;
    }
    setSaving(true);

    try {
      if (editingProject) {
        await adminApi.updateProject(editingProject.id, formData);
      } else {
        await adminApi.createProject(formData);
      }
      setDialogOpen(false);
      fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.deleteProject(id);
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      planned: 'badge-planned',
      in_progress: 'badge-in-progress',
      completed: 'badge-completed',
    };
    const labels = {
      planned: 'Planlanan',
      in_progress: 'Devam Ediyor',
      completed: 'Tamamlandı',
    };
    return <span className={classes[status]}>{labels[status]}</span>;
  };

  return (
    <div data-testid="admin-projects-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1
            className="text-2xl font-medium"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Projeler
          </h1>
          <p className="text-[#8e8e93] mt-1">
            Projeleri yönetin ve düzenleyin
          </p>
        </div>
        <Button
          onClick={handleOpenCreate}
          data-testid="create-project-btn"
          className="bg-[#041f60] hover:bg-[#b87333]"
        >
          <Plus size={18} className="mr-2" />
          Yeni Proje
        </Button>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white p-6 border border-[#d1d1d6] animate-pulse">
              <div className="h-40 bg-[#d1d1d6] mb-4"></div>
              <div className="h-4 bg-[#d1d1d6] w-48 mb-3"></div>
              <div className="h-3 bg-[#d1d1d6] w-32"></div>
            </div>
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="bg-white p-12 border border-[#d1d1d6] text-center">
          <p className="text-[#8e8e93] mb-4">Henüz proje eklenmemiş.</p>
          <Button
            onClick={handleOpenCreate}
            className="bg-[#041f60] hover:bg-[#b87333]"
          >
            <Plus size={18} className="mr-2" />
            İlk Projeyi Ekle
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project.id}
              data-testid={`project-admin-item-${project.id}`}
              className="bg-white border border-[#d1d1d6] overflow-hidden"
            >
              <img
                src={resolveImageUrl(project.image_url)}
                alt={project.title}
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium">{project.title}</h3>
                    <p className="text-sm text-[#8e8e93]">{project.location}</p>
                  </div>
                  {getStatusBadge(project.status)}
                </div>
                <p className="text-sm text-[#8e8e93] line-clamp-2 mb-4">
                  {project.description}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenEdit(project)}
                    data-testid={`edit-project-${project.id}`}
                    className="flex-1"
                  >
                    <Pencil size={16} className="mr-2" />
                    Düzenle
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`delete-project-${project.id}`}
                        className="border-red-500 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Projeyi Sil</AlertDialogTitle>
                        <AlertDialogDescription>
                          "{project.title}" projesini silmek istediğinizden emin misiniz?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>İptal</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(project.id)}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          Sil
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "var(--font-heading)" }}>
              {editingProject ? 'Projeyi Düzenle' : 'Yeni Proje Ekle'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} data-testid="project-form">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Proje Adı *</label>
                <Input
                  data-testid="project-title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="input-premium"
                  placeholder="Örn: Kadıköy Residence"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Konum *</label>
                  <Input
                    data-testid="project-location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    className="input-premium"
                    placeholder="Örn: Kadıköy, İstanbul"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Durum *</label>
                  <Select value={formData.status} onValueChange={handleStatusChange}>
                    <SelectTrigger data-testid="project-status" className="input-premium">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planned">Planlanan</SelectItem>
                      <SelectItem value="in_progress">Devam Ediyor</SelectItem>
                      <SelectItem value="completed">Tamamlandı</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Açıklama *</label>
                <Textarea
                  data-testid="project-description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="input-premium resize-none"
                  placeholder="Proje hakkında kısa açıklama..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Kapak Fotoğrafı *</label>
                <ImageUpload
                  value={formData.image_url}
                  onChange={(url) => setFormData((prev) => ({ ...prev, image_url: url }))}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Alan</label>
                  <Input
                    data-testid="project-area"
                    name="area"
                    value={formData.area}
                    onChange={handleChange}
                    className="input-premium"
                    placeholder="Örn: 4,200 m²"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Ünite Sayısı</label>
                  <Input
                    data-testid="project-units"
                    name="units"
                    value={formData.units}
                    onChange={handleChange}
                    className="input-premium"
                    placeholder="Örn: 36 Daire"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  data-testid="project-submit"
                  disabled={saving}
                  className="flex-1 bg-[#041f60] hover:bg-[#b87333]"
                >
                  {saving ? 'Kaydediliyor...' : editingProject ? 'Güncelle' : 'Ekle'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProjects;
